const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/**
 * A pause/play button for looping motion.
 *
 * By default it writes `data-motion-state="paused|playing"` on the closest `[data-motion-root]`, which the
 * section's CSS uses to set `animation-play-state`. With `data-motion-target="video"` it pauses and plays the
 * video rendered just before it instead. Either way it fires a bubbling `motion-toggle:change` event, so
 * scripted motion such as a carousel can follow along.
 *
 * Motion starts paused when the visitor prefers reduced motion, and follows that preference when it changes.
 * A video's button follows the video itself: other scripts pausing it, or a refused play(), show "Play".
 */
class MotionToggle extends HTMLElement {
  /** @type {HTMLButtonElement | null} */
  #button = null;

  /** @type {HTMLElement | null} */
  #root = null;

  /** @type {HTMLVideoElement | null} */
  #video = null;

  /** @type {MutationObserver | null} */
  #slideObserver = null;

  connectedCallback() {
    this.#button = this.querySelector('button');

    if (this.dataset.motionTarget === 'video') {
      const video = this.previousElementSibling;
      this.#video = video instanceof HTMLVideoElement ? video : null;
      this.#root = this.#video;
    } else {
      this.#root = this.closest('[data-motion-root]');
    }

    if (!this.#button || !this.#root) return;

    this.#button.addEventListener('click', this.#toggle);
    reducedMotion.addEventListener('change', this.#followPreference);
    this.#video?.addEventListener('play', this.#showPlaying);
    this.#video?.addEventListener('pause', this.#showPaused);
    this.#video?.addEventListener('lazy-video:blocked', this.#showPaused);

    // A button inside a carousel slide the slideshow hides with aria-hidden must leave the tab order too.
    const slide = this.closest('slideshow-slide');
    if (slide) {
      this.#slideObserver = new MutationObserver(() => this.#followSlide(slide));
      this.#slideObserver.observe(slide, { attributes: true, attributeFilter: ['aria-hidden'] });
      this.#followSlide(slide);
    }

    const state = this.#root.dataset.motionState;
    this.#apply(state ? state === 'paused' : reducedMotion.matches, { startVideo: false });
  }

  disconnectedCallback() {
    this.#button?.removeEventListener('click', this.#toggle);
    reducedMotion.removeEventListener('change', this.#followPreference);
    this.#video?.removeEventListener('play', this.#showPlaying);
    this.#video?.removeEventListener('pause', this.#showPaused);
    this.#video?.removeEventListener('lazy-video:blocked', this.#showPaused);
    this.#slideObserver?.disconnect();
    this.#slideObserver = null;
  }

  get paused() {
    return this.#root?.dataset.motionState === 'paused';
  }

  #toggle = () => this.#apply(!this.paused, { loadVideo: true });

  /** @param {Element} slide */
  #followSlide(slide) {
    if (slide.getAttribute('aria-hidden') === 'true') {
      this.#button?.setAttribute('tabindex', '-1');
    } else {
      this.#button?.removeAttribute('tabindex');
    }
  }

  /** @param {MediaQueryListEvent} event */
  #followPreference = (event) => this.#apply(event.matches);

  // These only report what the video did; they never start or stop it, so they cannot loop.
  #showPlaying = () => this.#render(false);

  #showPaused = () => {
    if (this.#video?.paused) this.#render(true);
  };

  /**
   * @param {boolean} paused
   * @param {{ startVideo?: boolean, loadVideo?: boolean }} [options] - The first sync leaves starting the video to
   *   the lazy loader. Only a click loads a video the lazy loader has not, such as one a variant change inserted.
   */
  #apply(paused, { startVideo = true, loadVideo = false } = {}) {
    this.#render(paused);

    if (this.#video) {
      if (paused) {
        pauseVideo(this.#video);
      } else if (startVideo) {
        playVideo(this.#video, loadVideo);
      }
    }
  }

  /** @param {boolean} paused */
  #render(paused) {
    if (!this.#button || !this.#root) return;

    const state = paused ? 'paused' : 'playing';
    if (this.#root.dataset.motionState === state && this.#button.getAttribute('aria-pressed') === String(paused)) return;

    this.#root.dataset.motionState = state;
    this.#button.setAttribute('aria-pressed', String(paused));
    this.#button.setAttribute('aria-label', paused ? this.dataset.playLabel : this.dataset.pauseLabel);

    this.dispatchEvent(new CustomEvent('motion-toggle:change', { bubbles: true, detail: { paused } }));
  }
}

/** @param {HTMLVideoElement} video */
function pauseVideo(video) {
  if (!video.dataset.src) video.pause();
  // A reveal video that never played is still transparent; keep its poster on screen.
  if (!video.classList.contains('is-playing')) video.classList.add('is-blocked');
}

/**
 * @param {HTMLVideoElement} video
 * @param {boolean} [load] - Load a video still waiting on lazy-video.js instead of leaving it to the loader.
 */
function playVideo(video, load = false) {
  video.classList.remove('is-blocked');
  // Hidden breakpoint copies never start.
  if (!video.getClientRects().length || video.closest('[data-lbl-v2-slide][aria-hidden="true"]')) return;

  const src = video.dataset.src;
  if (src) {
    // Otherwise lazy-video.js starts it once it is on screen.
    if (!load) return;

    video.src = src;
    video.removeAttribute('data-src');
    video.dataset.loaded = 'true';
    video.load();
  }

  video.play()?.catch(() => {
    if (!video.paused) return;
    if (!video.classList.contains('is-playing')) video.classList.add('is-blocked');
    video.dispatchEvent(new Event('lazy-video:blocked'));
  });
}

if (!customElements.get('motion-toggle')) {
  customElements.define('motion-toggle', MotionToggle);
}
